import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch } from 'react-redux';
import { platformActions } from '@clatter/platform';
import { primaryColor } from '../../config/theme';
import DropDown from '../DropDown/DropDown';
import SidebarExpandableNavLink from './SidebarExpandableNavLink';
import SidebarNavLink from './SidebarNavLink';

const StyledSidebar = styled.div`
  .sidebar-trigger {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    border: none;
    outline: none;
    padding: 0;
    margin: 0 16px 0 0;
    width: 32px;
    height: 32px;

    svg {
      font-size: 30px;
    }
  }

  .sidebar-overlay {
    transition: all 0.3s ease-in;
    position: fixed;
    top: ${({ theme }) => theme.pageHeader.height};
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(17, 17, 17, 0.36);
    opacity: 0;
    visibility: hidden;

    .sidebar-content {
      transition: all 0.3s ease-in;
      width: 265px;
      height: 100%;
      background-color: #fff;
      transform: translateX(-100%);
      padding: 20px 0;
      overflow: scroll;

      .tool-selector {
        padding: 0 30px 20px;
      }

      .sidebar-nav {
        display: flex;
        flex-direction: column;
        row-gap: 5px;
      }
    }

    &.sidebar-opened {
      opacity: 1;
      visibility: visible;

      .sidebar-content {
        transform: translateX(0);
      }
    }
  }
`;

const Sidebar = ({ menuItems, toolItems }) => {
  const dispatch = useDispatch();
  const [opened, setOpened] = useState(false);
  const overlayRef = useRef(null);
  const activeToolItem = toolItems.find((tool) =>
    tool?.url?.includes(window.location.origin),
  );

  const toggleOpened = () => setOpened(!opened);

  const handleOverlayClick = (event) => {
    if (overlayRef.current && overlayRef.current === event.target && opened) {
      toggleOpened();
    }
  };

  const handleToolChange = (tool) => {
    if (tool?.slug) {
      dispatch(platformActions.persisted.set({ type: 'lastUsedTool', data: tool.slug }));
    }
  };

  return (
    <StyledSidebar>
      <button className="sidebar-trigger" onClick={toggleOpened}>
        {opened ? <ArrowBackIcon /> : <MenuIcon />}
      </button>
      <div
        className={classNames('sidebar-overlay', {
          'sidebar-opened': opened,
        })}
        ref={overlayRef}
        onClick={handleOverlayClick}
      >
        <div className="sidebar-content">
          <div className="tool-selector">
            <DropDown hideActive options={toolItems} value={activeToolItem} noValueLabel="Select a Tool" onChange={handleToolChange} />
          </div>

          <div className="sidebar-nav">
            {menuItems &&
              Array.isArray(menuItems) &&
              menuItems.map((item) => {
                if (item.children) {
                  return <SidebarExpandableNavLink item={item} key={item.label}/>;
                }
                return <SidebarNavLink item={item} key={item.url} />;
              })}
          </div>
        </div>
      </div>
    </StyledSidebar>
  );
};

Sidebar.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string,
      icon: PropTypes.object.isRequired,
      exact: PropTypes.bool,
    }),
  ),
  toolItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ),
};

export default Sidebar;
